import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, useApi } from "@/lib/api";
import { CategorySelect, IGNORE } from "@/components/TrialBalanceUpload";

function EditTemplate({ tpl, cats, onClose, onSaved, onNewCat, src }) {
  const api = useApi();
  const [name, setName] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (tpl) {
      setName(tpl.name);
      setRows(Object.entries(tpl.mappings || {}).sort(([a], [b]) => a.localeCompare(b)).map(([account, category], i) => ({ key: i, account, category })));
    }
  }, [tpl]);
  const upd = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const save = async () => {
    const accts = rows.map((r) => r.account.trim()).filter(Boolean);
    if (new Set(accts).size !== accts.length) return toast.error("Each account name can only appear once");
    if (rows.some((r) => r.account.trim() && !r.category)) return toast.error("Choose a category for every account");
    setBusy(true);
    try {
      const mappings = Object.fromEntries(rows.filter((r) => r.account.trim()).map((r) => [r.account.trim(), r.category]));
      await api.put(`/tb/templates/${tpl.id}`, { name, mappings });
      toast.success("Template saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Dialog open={!!tpl} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="tb-edit-template-dialog">
        <DialogHeader><DialogTitle>Edit template</DialogTitle></DialogHeader>
        <label className="small-label" htmlFor="et-name">Template name</label>
        <Input id="et-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="tb-edit-template-name" />
        <div className="small-label mt-4">Account mappings ({rows.length})</div>
        <p className="text-xs text-slate-500">Account names must match the {src} exactly. Several accounts can share one category.</p>
        <div className="space-y-2 mt-2">
          {rows.map((r) => (
            <div key={r.key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 items-center" data-testid={`tb-edit-row-${r.key}`}>
              <Input value={r.account} onChange={(e) => upd(r.key, { account: e.target.value })} placeholder={`${src === "Quicken export" ? "Quicken" : "Trial balance"} account name`} className="h-8 text-xs rounded-sm" data-testid={`tb-edit-account-${r.key}`} />
              <CategorySelect value={r.category} cats={cats} testid={`tb-edit-category-${r.key}`}
                onChange={(c) => (c === "__new__" ? onNewCat((created) => upd(r.key, { category: created })) : upd(r.key, { category: c }))} />
              <Button variant="ghost" size="icon" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} data-testid={`tb-edit-remove-${r.key}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
        <Button variant="outline" className="rounded-sm mt-3 w-fit" onClick={() => setRows((rs) => [...rs, { key: Date.now(), account: "", category: IGNORE }])} data-testid="tb-edit-add-row"><Plus className="h-4 w-4 mr-2" />Add account</Button>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || name.trim().length < 2} data-testid="tb-edit-template-save">{busy ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TemplateManager({ templates, cats, reload, onNewCat, onDeleted, format = "trialbalance" }) {
  const api = useApi();
  const [edit, setEdit] = useState(null);
  const duplicate = async (t) => {
    const names = new Set(templates.map((x) => x.name.toLowerCase()));
    let name = `Copy of ${t.name}`;
    for (let i = 2; names.has(name.toLowerCase()); i += 1) name = `Copy of ${t.name} (${i})`;
    try {
      const r = await api.post("/tb/templates", { name, mappings: t.mappings || {}, format });
      toast.success(`Created "${name}" — rename and adjust it below`);
      await reload();
      setEdit(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Past imports are not affected.`)) return;
    try {
      await api.delete(`/tb/templates/${t.id}`);
      toast.success("Template deleted");
      onDeleted(t.id);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="panel mt-8" data-testid="tb-template-manager">
      <div className="px-5 py-4 border-b border-slate-200 small-label">Saved mapping templates</div>
      {templates.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500" data-testid="tb-no-templates">No templates yet. Load a {format === "quicken" ? "Quicken export" : "trial balance"}, map it, then use "Save as new template".</p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {templates.map((t) => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`tb-template-row-${t.id}`}>
                <td className="px-5 py-3 font-medium">{t.name}<div className="sm:hidden text-xs font-normal text-slate-500">{Object.keys(t.mappings || {}).length} accounts mapped</div></td>
                <td className="text-xs text-slate-500 hidden sm:table-cell">{Object.keys(t.mappings || {}).length} accounts mapped</td>
                <td className="text-xs text-slate-500 hidden sm:table-cell">Updated {new Date(t.updated_at).toLocaleDateString()}</td>
                <td className="pr-4 text-right whitespace-nowrap sm:whitespace-nowrap">
                  <div className="flex flex-col sm:flex-row sm:justify-end items-end">
                  <Button variant="ghost" size="sm" onClick={() => setEdit(t)} data-testid={`tb-template-edit-${t.id}`}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => duplicate(t)} data-testid={`tb-template-duplicate-${t.id}`}><Copy className="h-4 w-4 mr-1" />Duplicate</Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(t)} data-testid={`tb-template-remove-${t.id}`}><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <EditTemplate src={format === "quicken" ? "Quicken export" : "trial balance"} tpl={edit} cats={cats} onNewCat={onNewCat} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />
    </div>
  );
}
